/**
 * AOP 2027 Planning — the page's month columns (Anna, Oct 2026).
 *
 * The page plans from October 2026 to December 2027, so the months before October are history:
 * Jan–Sep 2026 is ONE group, collapsed by default to its last month (Sep 2026, a point-in-time
 * figure like every collapsed group). From October 2026 every month is its own column by default:
 * Q4 2026, then 2027 in the overview's own trimesters (T1–T3). Every group can still be toggled,
 * exactly as on the overview — `OrgChartTable` takes any `MonthGroup` list.
 */
import type { MonthGroup } from '../internalPlayground/OrgChartTable';

/** Months up to and including this one are the collapsed history group. */
export const AOP_HISTORY_END = '2026-09';

/** The year the AOP plans — the targets' FY column and summary tiles. */
export const AOP_PLAN_YEAR = '2027';

const TRIMESTER_BY_END_MONTH: Record<string, string> = { '04': 'T1', '08': 'T2', '12': 'T3' };

export function aopMonthGroups(months: string[]): MonthGroup[] {
  const groups: MonthGroup[] = [];
  const history = months.filter((m) => m <= AOP_HISTORY_END);
  if (history.length > 0) {
    groups.push({ key: history[history.length - 1], label: `${monthName(history[0])}–${monthName(history[history.length - 1])} '${yy(history[0])}`, months: history });
  }
  // The rest of the history year (Oct–Dec), then each later year in trimesters of four.
  const rest = months.filter((m) => m > AOP_HISTORY_END);
  let i = 0;
  while (i < rest.length) {
    const [year, mm] = rest[i].split('-');
    const sameYear = rest.filter((m) => m.startsWith(`${year}-`));
    if (year === AOP_HISTORY_END.slice(0, 4)) {
      groups.push({ key: sameYear[sameYear.length - 1], label: `${monthName(sameYear[0])}–${monthName(sameYear[sameYear.length - 1])} '${yy(rest[i])}`, months: sameYear });
      i += sameYear.length;
      continue;
    }
    const chunk = rest.slice(i, i + 4).filter((m) => m.startsWith(`${year}-`));
    const last = chunk[chunk.length - 1];
    groups.push({ key: last, label: `${TRIMESTER_BY_END_MONTH[last.slice(5)] ?? mm} '${year.slice(2)}`, months: chunk });
    i += chunk.length;
  }
  return groups;
}

/** Every group expanded except the history one. */
export function defaultExpandedGroupKeys(groups: MonthGroup[]): Set<string> {
  return new Set(groups.filter((g) => g.key > AOP_HISTORY_END).map((g) => g.key));
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthName = (monthKey: string) => MONTH_NAMES[Number(monthKey.slice(5, 7)) - 1] ?? monthKey;
const yy = (monthKey: string) => monthKey.slice(2, 4);
